import React from "react";
import { format } from "date-fns";
import Gender from "./Gender";
import Weight from "./Weight";
import NumberInput from "./NumberInput";
import BeverageLabel from "./BeverageLabel";
import TimeLastDrink from "./TimeLastDrink";

export default function App() {
  const [gender, setGender] = React.useState(null);
  const [weight, setWeight] = React.useState(null);
  const [beverages, setBeverages] = React.useState({
    beer: 0,
    wine: 0,
    spirit: 0,
  });
  const [timeSince, setTimeSince] = React.useState({
    hours: 0,
    minutes: 0,
  });
  const [ebac, setEbac] = React.useState(null);
  const [timeToSober, setTimeToSober] = React.useState(null);

  React.useEffect(() => {
    if (!gender || !weight || !beverages || !timeSince) {
      return;
    }

    const gramsPerMlOfAlcohol = 0.789;
    const a =
      beverages.beer * 250 * 0.05 * gramsPerMlOfAlcohol +
      beverages.wine * 100 * 0.12 * gramsPerMlOfAlcohol +
      beverages.spirit * 30 * 0.4 * gramsPerMlOfAlcohol;

    const vd = gender === "male" ? 0.71 * weight : 0.58 * weight;

    let T = parseFloat(timeSince.hours) + parseFloat(timeSince.minutes) / 60;
    if (isNaN(T)) {
      T = 0;
    }

    const beta = 0.15;
    const rawEbac = a / vd - beta * T;
    const ebac = Math.max(0, rawEbac);

    setEbac(ebac);

    const timeToSober = ebac > 0 ? ebac / beta : 0;
    setTimeToSober(timeToSober);
  }, [gender, weight, beverages, timeSince]);

  function handleBeverage(beverageName, beverageQty) {
    setBeverages((prevBeverages) => ({
      ...prevBeverages,
      [beverageName]: beverageQty,
    }));
  }

  function updateTimeChange(newHours, newMinutes) {
    newMinutes = newMinutes > 60 ? 59 : newMinutes;
    setTimeSince(() => ({
      hours: Number(newHours),
      minutes: Number(newMinutes),
    }));
  }

  function formatTimeInHoursAndMinutes(decimalHours) {
    if (decimalHours === 0) {
      return "zéro heure";
    }
    const hours = Math.floor(decimalHours);
    const minutes = Math.round((decimalHours - hours) * 60);
    const date = new Date(0, 0, 0, hours, minutes, 0);
    return format(date, "H'h'mm");
  }

  const displayEbac = ebac ? String(ebac?.toFixed(2)).replace(".", ",") : "-";
  const displayTimeToSober = timeToSober
    ? formatTimeInHoursAndMinutes(timeToSober)
    : "-";

  return (
    <>
      <div className="flex w-full text-center gap-2 sm:gap-4">
        <Gender
          onClick={() => setGender("male")}
          gender="male"
          selected={gender == "male"}
        />
        <Gender
          onClick={() => setGender("female")}
          gender="female"
          selected={gender == "female"}
        />
      </div>
      <div className="p-4 my-2 sm:my-4 bg-stone-100 rounded-lg">
        <Weight onChange={(newWeight) => setWeight(newWeight)} />
      </div>
      <div className="flex items-stretch gap-2 sm:gap-4 my-2 sm:my-4 ">
        <div className="w-1/3 text-center text-4xl sm:text-5xl bg-stone-100 rounded-lg px-2 sm:px-4 py-4 sm:py-6">
          <div className="cursor-default">🍺</div>
          <div className="mx-auto mt-2 sm:mt-4">
            <NumberInput
              beverageName="beer"
              handleBeverage={handleBeverage}
              quantity={beverages.beer}
              descriptionId="description-bière"
            />
            <BeverageLabel name="bière" abv={5} sizeInCl={25} />
          </div>
        </div>
        <div className="w-1/3 text-center text-4xl sm:text-5xl bg-stone-100 rounded-lg px-2 sm:px-4 py-4 sm:py-6">
          <div className="cursor-default">🍷</div>
          <div className="mx-auto mt-2 sm:mt-4">
            <NumberInput
              beverageName="wine"
              handleBeverage={handleBeverage}
              quantity={beverages.wine}
              descriptionId="description-vin"
            />
            <BeverageLabel name="vin" abv={12} sizeInCl={10} />
          </div>
        </div>
        <div className="w-1/3 text-center text-4xl sm:text-5xl bg-stone-100 rounded-lg px-2 sm:px-4 py-4 sm:py-6">
          <div className="cursor-default">🥃</div>
          <div className="mx-auto mt-2 sm:mt-4">
            <NumberInput
              beverageName="spirit"
              handleBeverage={handleBeverage}
              quantity={beverages.spirit}
              descriptionId="description-vin"
            />
            <BeverageLabel name="spiritueux" abv={40} sizeInCl={3} />
          </div>
        </div>
      </div>
      <div className="p-4 my-2 sm:my-4 bg-stone-100 rounded-lg">
        <TimeLastDrink
          hours={timeSince.hours}
          minutes={timeSince.minutes}
          updateTimeChange={updateTimeChange}
        />
      </div>
      <div className="mb-2 font-bold text-center w-full sm:text-lg cursor-default">
        Résultat
      </div>
      <div className="flex items-stretch gap-2 sm:gap-4">
        <div className="w-1/2 bg-pink-950 rounded-lg p-4 shadow-md flex flex-col justify-between">
          <div className="gap-2 flex items-center">
            <div className="text-white">
              <svg
                className="fill-current h-7 w-7"
                xmlns="http://www.w3.org/2000/svg"
                height="24"
                viewBox="0 -960 960 960"
                width="24"
                aria-hidden="true"
              >
                <path d="M480-100q-133 0-226.5-92T160-416q0-63 24.5-120.5T254-638l226-222 226 222q45 44 69.5 101.5T800-416q0 132-93.5 224T480-100Z" />
              </svg>
            </div>
            <div
              className="text-sm text-white sm:text-base w-full cursor-default"
              id="ebacLabel"
            >
              Alcoolémie estimée
            </div>
          </div>
          <div
            className="w-full text-center text-white h-full flex items-center my-2"
            aria-labelledby="ebacLabel"
          >
            <div
              className="flex flex-col self-center w-full"
              aria-live="polite"
              aria-atomic="true"
            >
              <span className="font-bold text-3xl">{displayEbac}</span>
              <span className="text-sm font-semibold cursor-default">
                g/L de sang
              </span>
            </div>
          </div>
        </div>
        <div className="w-1/2 bg-sky-700 rounded-lg p-4 shadow-md flex flex-col justify-between">
          <div className="gap-2 items-center flex">
            <div className="text-white">
              <svg
                className="fill-current h-7 w-7"
                xmlns="http://www.w3.org/2000/svg"
                height="24"
                viewBox="0 -960 960 960"
                width="24"
                aria-hidden="true"
              >
                <path d="m612-292 56-56-148-148v-184h-80v216l172 172ZM480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-400Zm0 320q133 0 226.5-93.5T800-480q0-133-93.5-226.5T480-800q-133 0-226.5 93.5T160-480q0 133 93.5 226.5T480-160Z" />
              </svg>
            </div>
            <div
              className="text-sm text-white sm:text-base w-full cursor-default"
              id="soberTimeLabel"
            >
              Temps à 0% d'alcool
            </div>
          </div>
          <div
            className="text-3xl w-full text-center font-bold text-white h-full flex items-center"
            aria-labelledby="soberTimeLabel"
          >
            <span
              className="self-center w-full"
              aria-live="polite"
              aria-atomic="true"
            >
              {displayTimeToSober}
            </span>
          </div>
        </div>
      </div>
    </>
  );
}